import { axios } from '@rossum/api-client';
import { isDevFeatureEnabled } from '../constants/dev';
import { roundOrNull } from './measureDownloadTime';

const WINDOW_MS = 60_000; // 60 seconds
const NETWORK_QUALITY_THRESHOLD_MS = 300;

type LatencySample = { timestamp: number; networkLatencyMs: number };

let samples: LatencySample[] = [];
let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;

const requestStartTimes = new WeakMap<object, number>();

export type LatencySummary = {
  networkLatencyMeanMs: number | null;
};

const prune = (now = performance.now()) => {
  const cutoff = now - WINDOW_MS;
  samples = samples.filter(s => s.timestamp > cutoff);
};

const recordSample = (
  config: object,
  responseHeaders: Record<string, unknown>
) => {
  const startMs = requestStartTimes.get(config);
  if (startMs === undefined) return;

  const endMs = performance.now();
  const browserTotalMs = endMs - startMs;

  const requestTimeHeader = responseHeaders['x-request-time'];
  if (typeof requestTimeHeader !== 'string') return;

  const serverTimeMs = parseFloat(requestTimeHeader) * 1000;

  if (!Number.isFinite(serverTimeMs) || serverTimeMs < 0) {
    if (isDevFeatureEnabled('console-log')) {
      // oxlint-disable-next-line no-console
      console.log('Latency: invalid X-Request-Time value:', requestTimeHeader);
    }
    return;
  }

  const networkLatencyMs = Math.max(0, browserTotalMs - serverTimeMs);
  const now = performance.now();

  samples.push({ timestamp: now, networkLatencyMs });
  prune(now);
};

export const startLatencyCollection = () => {
  if (requestInterceptorId !== null) return () => {};

  requestInterceptorId = axios.interceptors.request.use(config => {
    requestStartTimes.set(config, performance.now());
    return config;
  });

  responseInterceptorId = axios.interceptors.response.use(response => {
    recordSample(response.config, response.headers);
    return response;
  });

  return () => {
    if (requestInterceptorId !== null) {
      axios.interceptors.request.eject(requestInterceptorId);
      requestInterceptorId = null;
    }
    if (responseInterceptorId !== null) {
      axios.interceptors.response.eject(responseInterceptorId);
      responseInterceptorId = null;
    }
    samples = [];
  };
};

export const getLatencySummary = (): LatencySummary => {
  prune();

  if (samples.length === 0) {
    return { networkLatencyMeanMs: null };
  }

  const sum = samples.reduce((acc, s) => acc + s.networkLatencyMs, 0);
  const mean = sum / samples.length;

  return {
    networkLatencyMeanMs: roundOrNull(mean),
  };
};

export const getNetworkQuality = (): 'good' | 'poor' | 'unknown' => {
  const { networkLatencyMeanMs } = getLatencySummary();
  if (networkLatencyMeanMs === null) return 'unknown';
  return networkLatencyMeanMs < NETWORK_QUALITY_THRESHOLD_MS ? 'good' : 'poor';
};
